/**
 * Comments, replies and @-mentions.
 *
 * DATA DEFINITIONS
 *
 *   A CommentId is a String, unique across the workspace.
 *
 *   A CommentTarget is { projectId, path } or { projectId } alone.
 *   Interpretation: what the conversation is about — one file, or the project
 *   as a whole. Comments attach to a PATH rather than to a content hash, so a
 *   discussion survives the file being revised, which is exactly what an
 *   engineering review thread needs.
 *
 *   A Comment is a structure (see below). A comment with `parentId` undefined
 *   is a THREAD ROOT; one with a parentId is a REPLY to that root.
 *   Interpretation: we allow exactly two levels. Deeper nesting reads badly in
 *   a side panel and nobody has ever wanted it on a drawing review.
 *
 *   A Mention is a UserId extracted from the body text.
 *   Interpretation: someone who should be notified. Mentions are computed from
 *   the body when the comment is written and STORED, so that notification and
 *   search never have to re-parse prose.
 *
 *   A FileRef is the literal text "[[" <path> "]]" inside a body.
 *   Interpretation: "the file at this project-relative path, in this comment's
 *   own project, as it stood when the comment was written". A review note is
 *   mostly about other files ("same o-ring groove as [[Gun Assembly/Roof.PDF]]")
 *   and that reference has to stay clickable.
 *
 *   WHY A PATH AND NOT AN ID. There is no file id to point at: a file in
 *   Lattice *is* its path folded out of the change log (see change.ts). So the
 *   path is the only durable handle, and a reference survives a rename by
 *   being followed through the log rather than by being rewritten — see
 *   `followRenames`. Nothing in storage is ever edited to keep a reference
 *   true, which matters because the body is what the author wrote.
 *
 *   WHY "[[…]]". The body is plain text in Postgres and must round-trip
 *   unchanged, so the syntax may only use ordinary characters. Real paths here
 *   contain spaces ("Gun Assembly/…") and underscores but no brackets — 507
 *   paths in the live corpus, zero with a bracket — so double brackets delimit
 *   unambiguously without escaping, and a client that does not understand them
 *   still shows the reader a readable path.
 *
 *   A Comment is soft-deleted (`deletedAt` set, `body` blanked) rather than
 *   removed, so replies beneath it keep their context and the provenance of a
 *   review is not silently rewritten.
 */
import { parsePath } from './path.js';
import { ROSTER } from './user.js';
import {} from './change.js';
import { ok, err } from './result.js';
import { byNewestInstant, laterOf } from './instant.js';
/** The longest a comment may be. Long enough for a real review note. */
export const MAX_COMMENT_LENGTH = 4000;
/**
 * Mention handles, longest first.
 *
 * We match on handles derived from the roster rather than on a generic
 * "@\w+" pattern, because engineers write "@Bhargava Ram" and "@jishnu"
 * interchangeably and we want both to resolve to the same person. Matching
 * longest-first stops "@vishnu" from shadowing "@vishnukumar".
 */
const HANDLES = ROSTER
    .flatMap(u => {
    const first = u.name.split(/\s+/)[0].toLowerCase();
    const full = u.name.toLowerCase().replace(/\s+/g, '');
    return [
        { handle: u.id, userId: u.id },
        { handle: first, userId: u.id },
        { handle: full, userId: u.id },
    ];
})
    .filter((h, i, all) => all.findIndex(o => o.handle === h.handle) === i)
    .sort((a, b) => b.handle.length - a.handle.length);
/**
 * Find the people mentioned in a comment body.
 *
 * parseMentions : String -> [UserId]   (de-duplicated, in order of appearance)
 */
export function parseMentions(body) {
    const found = [];
    const lower = body.toLowerCase();
    // Scan each "@" once and take the longest handle that matches there.
    for (let i = 0; i < lower.length; i++) {
        if (lower[i] !== '@')
            continue;
        for (const { handle, userId } of HANDLES) {
            if (!lower.startsWith(handle, i + 1))
                continue;
            const after = lower[i + 1 + handle.length];
            // A handle must not run into more word characters ("@jishnux" is nobody).
            if (after !== undefined && /[a-z0-9]/.test(after))
                continue;
            if (!found.includes(userId))
                found.push(userId);
            i += handle.length;
            break;
        }
    }
    return found;
}
export function segmentBody(body) {
    const out = [];
    const lower = body.toLowerCase();
    let plainFrom = 0;
    for (let i = 0; i < body.length; i++) {
        if (lower[i] !== '@')
            continue;
        const hit = HANDLES.find(({ handle }) => {
            if (!lower.startsWith(handle, i + 1))
                return false;
            const after = lower[i + 1 + handle.length];
            return after === undefined || !/[a-z0-9]/.test(after);
        });
        if (!hit)
            continue;
        if (i > plainFrom)
            out.push({ text: body.slice(plainFrom, i) });
        const end = i + 1 + hit.handle.length;
        out.push({ text: body.slice(i, end), userId: hit.userId });
        plainFrom = end;
        i = end - 1;
    }
    if (plainFrom < body.length)
        out.push({ text: body.slice(plainFrom) });
    return out;
}
/* ------------------------------------------------------------------ */
/* File references                                                     */
/* ------------------------------------------------------------------ */
/** The delimiters of a FileRef. Exported so one parser cannot drift from another. */
export const FILE_REF_OPEN = '[[';
export const FILE_REF_CLOSE = ']]';
/**
 * Write a reference to a file, for insertion into a body.
 * formatFileRef : Path -> String
 */
export const formatFileRef = (path) => `${FILE_REF_OPEN}${path}${FILE_REF_CLOSE}`;
/**
 * Read the text between "[[" and "]]" as a path, or undefined when it is not
 * one. Whitespace around it is forgiving because people type these by hand as
 * well as picking them; everything else is exactly `parsePath`'s rules, so a
 * reference can never name something a file could not be.
 * parseFileRefPath : String -> Path | undefined
 */
export function parseFileRefPath(inner) {
    if (inner.includes('\n'))
        return undefined;
    const parsed = parsePath(inner.trim());
    return parsed.ok ? parsed.value : undefined;
}
/**
 * Every file referenced by a body, de-duplicated, in order of appearance.
 * fileRefsIn : String -> [Path]
 */
export function fileRefsIn(body) {
    const found = [];
    let at = 0;
    for (;;) {
        const open = body.indexOf(FILE_REF_OPEN, at);
        if (open === -1)
            return found;
        const close = body.indexOf(FILE_REF_CLOSE, open + FILE_REF_OPEN.length);
        if (close === -1)
            return found;
        const path = parseFileRefPath(body.slice(open + FILE_REF_OPEN.length, close));
        if (path && !found.includes(path))
            found.push(path);
        at = close + FILE_REF_CLOSE.length;
    }
}
/**
 * Where a referenced file lives NOW.
 *
 * A reference holds a path, and paths move. Every rename is a row in the log,
 * so we replay the renames recorded AFTER the reference was written and follow
 * the file to its current home. Renames recorded BEFORE it are deliberately
 * not followed: a path written today names today's file, even if some older
 * file once lived there and moved away.
 *
 * `since` is the referencing comment's own seq — one sequence orders changes
 * and comments alike (see architecture §2), which is exactly what makes "after
 * it was written" answerable at all.
 *
 * followRenames : [Change] ProjectId Path Seq -> Path
 */
export function followRenames(changes, projectId, path, since = 0) {
    const renames = changes
        .filter(c => c.kind === 'renamed' && c.projectId === projectId && c.seq > since)
        .sort((a, b) => a.seq - b.seq);
    let current = path;
    let at = since;
    // Taking each matching rename in seq order walks a chain of them. `at` only
    // ever increases, so a path that cycles (A→B→A→C, which the live log really
    // does contain) resolves to C rather than looping.
    for (const rename of renames) {
        if (rename.seq <= at)
            continue;
        if (rename.prevPath?.toLowerCase() !== current.toLowerCase())
            continue;
        current = rename.path;
        at = rename.seq;
    }
    return current;
}
/**
 * The longest a half-typed token may get before we stop offering completions.
 * Past this the person is plainly writing prose, not picking something.
 */
const MAX_TRIGGER_QUERY = 48;
/**
 * What the person is in the middle of typing at the caret, if anything.
 *
 * A trigger must start a word — the character before it is the start of the
 * body or whitespace — which is what keeps "https://…" and "Drawing/Roof.PDF"
 * from opening a picker mid-sentence. A mention's query breaks at whitespace
 * (handles are one word); a file's does not, because real paths here contain
 * spaces.
 *
 * triggerAt : String Number -> Trigger | undefined
 */
export function triggerAt(text, caret) {
    const upto = text.slice(0, Math.max(0, Math.min(caret, text.length)));
    const floor = Math.max(0, upto.length - (MAX_TRIGGER_QUERY + 1));
    for (let i = upto.length - 1; i >= floor; i--) {
        const ch = upto[i];
        if (ch === '\n')
            return undefined;
        if (ch !== '@' && ch !== '/')
            continue;
        if (i > 0 && !/\s/.test(upto[i - 1]))
            return undefined;
        const query = upto.slice(i + 1);
        if (ch === '@' && /\s/.test(query))
            return undefined;
        return { kind: ch === '@' ? 'mention' : 'file', from: i, query };
    }
    return undefined;
}
/**
 * Accept a completion: replace the half-typed token with `token`, leaving the
 * caret past a single trailing space so the person can keep typing.
 * Returns the new body and where the caret belongs in it.
 * applyTrigger : String Trigger Number String -> {text, caret}
 */
export function applyTrigger(text, trigger, caret, token) {
    const tail = text.slice(caret);
    // Completing in the middle of a sentence must not double the space that is
    // already there.
    const head = `${text.slice(0, trigger.from)}${token}${tail.startsWith(' ') ? '' : ' '}`;
    return { text: `${head}${tail}`, caret: head.length + (tail.startsWith(' ') ? 1 : 0) };
}
/**
 * Check a proposed comment body before it is written.
 * validateBody : String -> Result<String>   (returns the trimmed body)
 */
export function validateBody(body) {
    const trimmed = body.trim();
    if (trimmed.length === 0)
        return err('a comment cannot be empty');
    if (trimmed.length > MAX_COMMENT_LENGTH)
        return err(`a comment cannot exceed ${MAX_COMMENT_LENGTH} characters`);
    return ok(trimmed);
}
/**
 * Group a flat list of comments into threads.
 *
 * Replies whose root is missing (which can only happen mid-sync, when the
 * delta arrived out of order) are promoted to roots rather than dropped — the
 * user sees their text, never a hole.
 * buildThreads : [Comment] -> [Thread]   (most recently active first)
 */
export function buildThreads(comments) {
    const roots = comments.filter(c => !c.parentId);
    const rootIds = new Set(roots.map(c => c.id));
    const orphans = comments.filter(c => c.parentId && !rootIds.has(c.parentId));
    const repliesByRoot = new Map();
    for (const c of comments) {
        if (!c.parentId || !rootIds.has(c.parentId))
            continue;
        const list = repliesByRoot.get(c.parentId);
        if (list)
            list.push(c);
        else
            repliesByRoot.set(c.parentId, [c]);
    }
    const threads = [...roots, ...orphans].map(root => {
        const replies = (repliesByRoot.get(root.id) ?? []).sort((a, b) => a.seq - b.seq);
        const all = [root, ...replies];
        return {
            root,
            replies,
            lastAt: all.reduce((m, c) => laterOf(c.at, m), root.at),
            participants: [...new Set(all.map(c => c.authorId))],
        };
    });
    return threads.sort((a, b) => byNewestInstant(a.lastAt, b.lastAt));
}
/** Count the comments in a thread, root included. threadSize : Thread -> Number */
export const threadSize = (t) => 1 + t.replies.length;
/**
 * The comments that should raise a notification for one person.
 *
 * A person is notified when they are @-mentioned, or when someone replies in a
 * thread they have taken part in. They are never notified about their own
 * writing — the commonest way a notification system becomes noise.
 * notifiableFor : [Thread] UserId -> [Comment]
 */
export function notifiableFor(threads, me) {
    const out = [];
    for (const t of threads) {
        const involved = t.participants.includes(me);
        for (const c of [t.root, ...t.replies]) {
            if (c.authorId === me || c.deletedAt)
                continue;
            if (c.mentions.includes(me) || (involved && c.parentId))
                out.push(c);
        }
    }
    return out.sort((a, b) => byNewestInstant(a.at, b.at));
}
/**
 * The most recent comments across the whole workspace, for the "Recent
 * comments" feed. Deleted comments are excluded.
 * recentComments : [Comment] Number -> [Comment]
 */
export const recentComments = (comments, limit = 50) => comments
    .filter(c => !c.deletedAt)
    .sort((a, b) => byNewestInstant(a.at, b.at))
    .slice(0, limit);
//# sourceMappingURL=comment.js.map